<template>
  <div class="collapsed-text">
    <rte-content
      class="collapsed-text__content"
      :class="{
        [$class('collapsed')]: !readMore.enabled && isCollapsed
      }"
      :content="text"
    />

    <button
      v-show="readMore.overflow && isCollapsed"
      type="button"
      class="collapsed-text__read-more"
      aria-hidden="true"
      @click="readMore.enabled = !readMore.enabled"
    >
      <span
        class="text-body-s-underline"
        v-text="readMoreLabel"
      />
    </button>
  </div>
</template>

<script>

/**
 * Vue: Collapsed text (collapsed-text)
 * -----------------------------------------------------------------------------
 * Collapsed text with read more/less button.
 * - Component settings: snippet / dynamic / async / scroll
 *
 */
import RteContent from '~async/rte-content/rte-content'

export default {
  name: 'CollapsedText',

  components: { RteContent },

  props: {
    text: {
      type: String,
      default: '',
    },
    isCollapsed: {
      type: Boolean,
      default: true,
    },
    line: {
      type: Number,
      default: 11,
    },
  },

  data() {
    return {
      readMore: {
        overflow: true,
        enabled: false,
      },
    }
  },

  computed: {

    /**
     * Compute text for read more button text.
     * @returns {String}
     */
    readMoreLabel() {
      return this.readMore.enabled
        ? this.$string('general.read_more.read_less')
        : this.$string('general.read_more.read_more')
    },
  },

  watch: {

    /**
     * Watches isCollapsed prop.
     */
    isCollapsed() {
      if (!this.isCollapsed) {
        return
      }

      this.checkDescriptionOverflow()
    },
  },

  mounted() {
    if (!this.isCollapsed) {
      return
    }

    this.checkDescriptionOverflow()
  },

  methods: {

    /**
     * Check if the description text is overflowing.
     * - Checks if the description text is larger than 2 lines tall or not.
     * - If it is not larger than 2 lines tall, then all the text is showing and
     *   we can update out read more state to hide the Read More/Read Less
     *   button.
     */
    checkDescriptionOverflow() {
      const element = document.querySelector('.collapsed-text__content')

      if (!element) {
        return
      }

      const { lineHeight } = getComputedStyle(element)
      const lineHeightNumber = parseInt(lineHeight)

      const totalCollapsedHeight = lineHeightNumber * this.line

      const isDescriptionOverflow = element.scrollHeight > totalCollapsedHeight

      this.readMore.overflow = isDescriptionOverflow

      document.documentElement.style.setProperty(
        '--collapsed-text-line',
        this.line,
      )
    },
  },
}
</script>

<style lang="scss">
@import './collapsed-text';
</style>
